import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ButtonBase } from '@mui/material';
import {
    RM3_BED,
    RM3_RIBBON_ORDER,
    applyRm3Filters,
    assignRibbons,
    availableFilters,
    buildRm3Containers,
    cardBedOf,
    cardTitleOf,
    dealFacts,
    sortRm3Containers,
    splitByPayment,
    splitCardsIntoRooms,
} from 'arbitrip-common/client/utils/HotelDealsRm3';

import Analytics from 'arbitrip-common/client/analytics';

import ProfileStore from '@/stores/ProfileStore';
import SearchStore from '@/stores/SearchStore';
import RouterWrapper from '@/utils/RouterWrapper';
import PointsToggleComponent from '@/components/pages/results/PointsToggleComponent.react';

import DealsTitle from '../DealsTitle.react';
import rm3FiltersReducer, { RM3_ACTION, initialRm3FiltersState, isAnyRm3FilterOn } from './rm3FiltersReducer';
import useRm3Book from './useRm3Book';
import useRm3PriceState from './useRm3PriceState';
import Rm3SortSelect from './Rm3SortSelect';
import Rm3FilterBar from './Rm3FilterBar';
import Rm3RoomContainer from './Rm3RoomContainer';
import Rm3DebugOtherRooms from './Rm3DebugOtherRooms';
import Rm3RoomSkeleton from './Rm3RoomSkeleton';
import Rm3RoomDetailsDialog from './Rm3RoomDetailsDialog';
import RIBBON_CSS_VARIABLES from './rm3RibbonVariables';
import { BED_ICON_KIND, IconReset } from './Rm3Icons';

import './hotelDealsRm3.scss';

// Principal 2026-10-19: sort hidden for now (default price); flip to true to restore the dropdown.
const SHOW_SORT_SELECT = false;
const SKELETONS_WITHOUT_ROOMS = 2;
const SKELETONS_BELOW_ROOMS = 1;
const BED_PART_SEPARATOR = ' · ';
const OTHER_ROOMS_KEY = 'rm3-other-rooms';
const EMPTY_STATE_IMAGE = '../../img/recent_searches/recent_searches_empty.svg';
const FLAT_CARD_ID = 'rm3-flat';

const SECTION_PRE_PAY = 'pre';
const SECTION_POST_PAY = 'post';

function allDealsOf(containers) {
    return containers.flatMap((container) => container.cards.flatMap((card) => card.deals));
}

// Principal 2026-10-20: a card with no bed record of its own shows no bed line (no container prose fallback).
function bedTextOf(card) {
    const bed = cardBedOf(card);
    const parts = bed ? [bed.configValue, bed.typeValue].filter(Boolean) : [];
    return parts.length ? parts.join(BED_PART_SEPARATOR) : null;
}

// Spec-v2 §1.2: twin art only when every token of the card's own record is twin; never parsed from the bed text.
function bedIconKindOf(card) {
    const bed = cardBedOf(card);
    const twinOnly = !!bed && bed.bedTypes.length > 0 && bed.bedTypes.every((token) => token === RM3_BED.TWIN);
    return twinOnly ? BED_ICON_KIND.TWIN : BED_ICON_KIND.DOUBLE;
}

/**
 * Card titles, bed lines and the multi-card flag, computed ONCE per data update on the unfiltered containers
 * (orchestrator decision): a filter must never rename a card, nor flip a container between the one-card and
 * the many-card layout. `cardIdByDealId` links each deal of the flat list back to its card.
 */
function containerMetaOf(containers) {
    const meta = new Map();
    containers.forEach((container) => {
        const cards = new Map();
        const cardIdByDealId = new Map();
        container.cards.forEach((card) => {
            cards.set(card.cardId, {
                title: cardTitleOf(card, container),
                bedText: bedTextOf(card),
                bedKind: bedIconKindOf(card),
            });
            card.deals.forEach((deal) => cardIdByDealId.set(deal.id, card.cardId));
        });
        const first = container.cards[0];
        meta.set(container.key, {
            multiCard: container.cards.length > 1,
            singleBedText: first ? cards.get(first.cardId).bedText : null,
            singleBedKind: first ? cards.get(first.cardId).bedKind : null,
            cards,
            cardIdByDealId,
        });
    });
    return meta;
}

/** Spec-v2 §1.1: a room's visible deals as ONE list, in the common sorter's order (render and analytics alike). */
function flatDealsOf(container, sort) {
    const deals = container.cards.flatMap((card) => card.deals);
    const [flat] = sortRm3Containers([{ key: container.key, cards: [{ cardId: FLAT_CARD_ID, deals }] }], sort);
    return flat.cards[0].deals;
}

function flatRoomsOf(containers, sort, section) {
    return sortRm3Containers(containers, sort).map((container) => ({
        container,
        section,
        deals: flatDealsOf(container, sort),
    }));
}

function clampPhotoIndex(index, count) {
    return count > 0 ? Math.min(Math.max(index || 0, 0), count - 1) : 0;
}

function sortOtherRooms(otherRooms, sort) {
    if (!otherRooms) {
        return null;
    }
    const [wrapped] = sortRm3Containers(
        [{ key: OTHER_ROOMS_KEY, cards: [{ cardId: OTHER_ROOMS_KEY, deals: otherRooms.deals }] }],
        sort,
    );
    return { ...otherRooms, deals: wrapped.cards[0].deals };
}

function isTravelPolicyEnabled(travelPolicy) {
    return !!(
        travelPolicy &&
        (travelPolicy.default || travelPolicy.place) &&
        travelPolicy.currency &&
        travelPolicy.price
    );
}

/** Today's `HotelPage.getDealsPlaceholderRender`, for a finished recheck that left no RM3 room. */
function NoDealsPlaceholder({ searchTerms }) {
    const { t } = useTranslation();
    const resultsRoute = RouterWrapper.buildResultsRoute(searchTerms, SearchStore.getToken());
    return (
        <div className="no-valid-deals-wrapper">
            <img className="empty-state-image" src={EMPTY_STATE_IMAGE} alt="" />
            <div>{t('NoAvailabilityOnTheseDates')}</div>
            <div>
                {t('TryChangeDates')} <Link to={resultsRoute}>{t('SelectDifferentHotel')}</Link>
            </div>
        </div>
    );
}

/**
 * ROOM MAPPING 3.0 "CHOOSE YOUR ROOM" — replaces the hotel page's deals list for a company with
 * `company.settings.enable_room_mapping_3_0` (spec `frontend/ui-specs/hotel-deals-rm3/spec.md`). Every grouping,
 * ribbon, filter, split and sort comes from `arbitrip-common/client/utils/HotelDealsRm3`; prices are today's
 * block (`Rm3DealPrice`). Deals without RM3 data are shown only in debug mode.
 */
function HotelDealsRm3({
    deals,
    hotel,
    profile,
    basket,
    searchTerms,
    travelPolicy,
    dealsLoading,
    infoOnly,
    infoBusy,
    bannedDealIds,
    arbitripPointsApplied,
    contractClientCompanyId,
    reservationManipulation,
    debugMode,
    isRtl,
}) {
    const { t } = useTranslation();
    const [state, dispatch] = useReducer(rm3FiltersReducer, undefined, initialRm3FiltersState);
    // Spec P2.1: one room-details modal per page; `openCount` keys a fresh mount for every open.
    const [details, setDetails] = useState({ open: false, openCount: 0, key: null, section: null, index: 0 });
    const priceState = useRm3PriceState(arbitripPointsApplied);
    const { book, addToList, removeFromList } = useRm3Book({
        hotel,
        profile,
        searchTerms,
        travelPolicy,
        contractClientCompanyId,
        reservationManipulation,
    });

    const hotelId = hotel && hotel.id;
    const previousHotelId = useRef(hotelId);
    useEffect(() => {
        if (previousHotelId.current !== hotelId) {
            previousHotelId.current = hotelId;
            dispatch({ type: RM3_ACTION.RESET });
            setDetails((previous) => (previous.open ? { ...previous, open: false } : previous));
        }
    }, [hotelId]);

    // Principal 2026-10-23: each card is its own room, so everything below sees the split list.
    const built = useMemo(() => {
        const result = buildRm3Containers(deals, hotel, { bannedDealIds });
        return { ...result, containers: splitCardsIntoRooms(result.containers) };
    }, [deals, hotel, bannedDealIds]);
    const meta = useMemo(() => containerMetaOf(built.containers), [built]);
    const ribbons = useMemo(
        () => assignRibbons(built.containers, { account: profile, now: Date.now() }),
        [built, profile],
    );

    const otherRooms = debugMode ? built.otherRooms : null;
    const visibleDeals = useMemo(
        () => allDealsOf(built.containers).concat(otherRooms ? otherRooms.deals : []),
        [built, otherRooms],
    );

    const topPicks = useMemo(() => {
        const present = new Set(ribbons.values());
        return RM3_RIBBON_ORDER.filter((kind) => present.has(kind));
    }, [ribbons]);

    const available = useMemo(
        () => availableFilters(built.containers, built.otherRooms, ribbons, { includeOtherRooms: !!debugMode }),
        [built, ribbons, debugMode],
    );

    const view = useMemo(() => {
        const filtered = applyRm3Filters(built.containers, otherRooms, state.filters, ribbons);
        const split = splitByPayment(filtered.containers);
        return {
            prePay: flatRoomsOf(split.prePay, state.sort, SECTION_PRE_PAY),
            postPay: flatRoomsOf(split.postPay, state.sort, SECTION_POST_PAY),
            multipleKinds: split.multipleKinds,
            otherRooms: sortOtherRooms(filtered.otherRooms, state.sort),
        };
    }, [built, otherRooms, state.filters, state.sort, ribbons]);

    const displayOrder = useMemo(() => {
        const order = new Map();
        const rendered = view.prePay
            .concat(view.postPay)
            .flatMap((room) => room.deals)
            .concat(view.otherRooms ? view.otherRooms.deals : []);
        rendered.forEach((deal) => order.set(deal.id, order.size + 1));
        return order;
    }, [view]);

    const context = useMemo(
        () => ({
            profile,
            basket,
            travelPolicy,
            arbitripPointsApplied,
            contractClientCompanyId,
            priceState,
            infoBusy,
            book,
            addToList,
            removeFromList,
        }),
        [
            profile,
            basket,
            travelPolicy,
            arbitripPointsApplied,
            contractClientCompanyId,
            priceState,
            infoBusy,
            book,
            addToList,
            removeFromList,
        ],
    );

    const findRoom = useCallback(
        (key, section) =>
            (section === SECTION_POST_PAY ? view.postPay : view.prePay).find((room) => room.container.key === key) ||
            null,
        [view],
    );

    const openDetails = (key, section, index) => {
        const room = findRoom(key, section);
        if (!room || !room.container.details.room) {
            return;
        }
        const { container } = room;
        if (hotel) {
            Analytics.actions.views.roomInfoDialog(profile?.company, hotel, container.details.room);
        }
        setDetails((previous) => ({
            open: true,
            openCount: previous.openCount + 1,
            key,
            section,
            index: clampPhotoIndex(index, container.details.images.length),
            snapshot: { container, meta: meta.get(key) },
        }));
    };

    const closeDetails = useCallback(() => {
        setDetails((previous) => (previous.open ? { ...previous, open: false } : previous));
    }, []);

    // Spec P2.4: the modal's rows get the page context; only Book closes the modal first (lazy recheck owns the page).
    const detailsContext = useMemo(
        () => ({
            ...context,
            book: (deal, order) => {
                closeDetails();
                context.book(deal, order);
            },
        }),
        [context, closeDetails],
    );

    // Spec P2.4: re-derived on every render so rechecks reach the modal; a vanished room keeps its snapshot.
    const liveDetailsRoom = details.key ? findRoom(details.key, details.section) : null;
    const detailsContainer = liveDetailsRoom
        ? liveDetailsRoom.container
        : details.snapshot && details.snapshot.container;
    const detailsMeta = (liveDetailsRoom && meta.get(details.key)) || (details.snapshot && details.snapshot.meta);

    const hasRooms = built.containers.length > 0;
    const showControls = hasRooms || dealsLoading;
    const anyFilterOn = isAnyRm3FilterOn(state.filters);
    const cancellationDisabled = !visibleDeals.some((deal) => dealFacts(deal).refundableTill);
    const showInPolicy = isTravelPolicyEnabled(travelPolicy) && !ProfileStore.isLeisureExperienceEnabled();
    const showPayAtHotel = visibleDeals.some((deal) => !!deal.post_pay);
    // While loading, an empty filter result shows the loaders; "No rooms match" waits for the finished recheck.
    const skeletonCount = hasRooms ? SKELETONS_BELOW_ROOMS : SKELETONS_WITHOUT_ROOMS;
    const nothingMatches = !dealsLoading && hasRooms && view.prePay.length === 0 && view.postPay.length === 0;

    const renderRooms = (rooms) => (
        <div className="rm3-rooms">
            {rooms.map(({ container, section, deals: roomDeals }) => (
                <Rm3RoomContainer
                    key={container.key}
                    container={container}
                    deals={roomDeals}
                    meta={meta.get(container.key)}
                    ribbons={ribbons}
                    displayOrder={displayOrder}
                    context={context}
                    onOpenDetails={(index) => openDetails(container.key, section, index)}
                />
            ))}
        </div>
    );

    return (
        <div className="rm3-section" style={RIBBON_CSS_VARIABLES}>
            <div className="title-wrapper rm3-header">
                <div className="title">{t('ChooseYourRoom')}</div>
                <div className="rm3-header__controls">
                    <PointsToggleComponent withTooltip />
                    {SHOW_SORT_SELECT && showControls && (
                        <Rm3SortSelect
                            value={state.sort}
                            onChange={(value) => dispatch({ type: RM3_ACTION.SET_SORT, value })}
                            cancellationDisabled={cancellationDisabled}
                        />
                    )}
                </div>
            </div>

            {showControls && (
                <Rm3FilterBar
                    state={state}
                    dispatch={dispatch}
                    topPicks={topPicks}
                    available={available}
                    showInPolicy={showInPolicy}
                    showPayAtHotel={showPayAtHotel}
                    anyFilterOn={anyFilterOn}
                />
            )}

            {!infoOnly && !dealsLoading && !hasRooms && <NoDealsPlaceholder searchTerms={searchTerms} />}

            {nothingMatches && (
                <div className="rm3-no-match" data-testid="rm3-no-rooms-match">
                    <div>{t('HotelDealsRm3.NoRoomsMatch')}</div>
                    <ButtonBase
                        className="rm3-reset"
                        onClick={() => dispatch({ type: RM3_ACTION.RESET_FILTERS })}
                        disableRipple
                    >
                        {t('HotelDealsRm3.ResetFilters')}
                        <IconReset />
                    </ButtonBase>
                </div>
            )}

            {view.multipleKinds ? (
                <>
                    <div data-testid="rm3-section-pre-pay">
                        <DealsTitle title={t('PrePayRates')}>{renderRooms(view.prePay)}</DealsTitle>
                    </div>
                    <div data-testid="rm3-section-post-pay">
                        <DealsTitle title={t('PostPayRates')}>{renderRooms(view.postPay)}</DealsTitle>
                    </div>
                </>
            ) : (
                renderRooms(view.prePay.concat(view.postPay))
            )}

            {dealsLoading && (
                <div className="rm3-rooms">
                    {Array.from({ length: skeletonCount }).map((_, index) => (
                        <Rm3RoomSkeleton key={`rm3-skeleton-${index}`} isRtl={isRtl} />
                    ))}
                </div>
            )}

            {view.otherRooms && (
                <Rm3DebugOtherRooms otherRooms={view.otherRooms} displayOrder={displayOrder} context={context} />
            )}

            {detailsContainer && detailsMeta && (
                <Rm3RoomDetailsDialog
                    key={details.openCount}
                    open={details.open}
                    onClose={closeDetails}
                    container={detailsContainer}
                    unfilteredContainer={built.containers.find((candidate) => candidate.key === details.key)}
                    meta={detailsMeta}
                    deals={liveDetailsRoom ? liveDetailsRoom.deals : []}
                    startIndex={details.index}
                    ribbons={ribbons}
                    displayOrder={displayOrder}
                    context={detailsContext}
                    profile={profile}
                    debugMode={debugMode}
                />
            )}
        </div>
    );
}

export default HotelDealsRm3;
